import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import './Login.css';

function Login({ isAuthenticated, onLogin }) {
  const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_BASE_URL) {
      return process.env.REACT_APP_API_BASE_URL;
    }
    return '/api/v1';
  };

  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const redirectPath = location.state?.from?.pathname || '/monitoring';

  if (isAuthenticated) {
    return <Navigate to={redirectPath} replace />;
  }

  const handleLogin = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (!username.trim() || !password) {
      setErrorMessage('Username and password are required.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${getApiBaseUrl()}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: username.trim(),
          password
        })
      });

      const contentType = response.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');
      const data = isJson ? await response.json() : null;
      const text = isJson ? '' : await response.text();

      if (!response.ok) {
        const backendError = data?.error || data?.message;
        const proxyError = text && text.startsWith('Proxy error');
        throw new Error(
          backendError ||
            (proxyError
              ? 'Backend is not reachable. Start the API server on http://localhost:8080 and try again.'
              : 'Login failed')
        );
      }

      const token =
        data?.token ||
        data?.access_token ||
        data?.jwt ||
        data?.data?.token ||
        data?.result?.token;
      if (!token) {
        const backendMessage = data?.error || data?.message;
        if (backendMessage) {
          throw new Error(backendMessage);
        }

        const htmlLike = !isJson && /<html|<!doctype/i.test(text || '');
        const looksLikeHealthPayload = isJson && data?.status === 'ok' && data?.service;

        if (looksLikeHealthPayload) {
          throw new Error('Login request reached API health endpoint instead of /login. Verify API rewrite rules and base URL.');
        }

        const payloadHint = isJson && data ? ` Response keys: ${Object.keys(data).join(', ') || '(none)'}.` : '';
        throw new Error(
          htmlLike
            ? 'Login request reached a web page instead of the API. Set REACT_APP_API_BASE_URL to https://api.remu.fi/api/v1 and redeploy frontend.'
            : `Login response did not include a token.${payloadHint}`
        );
      }

      onLogin(token);
      navigate(redirectPath, { replace: true });
    } catch (error) {
      setErrorMessage(error.message || 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <h1>Login Required</h1>
        <p>Monitoring and Reporting are available only after login.</p>

        <form className="login-form" onSubmit={handleLogin}>
          <label className="login-label" htmlFor="username">Username</label>
          <input
            id="username"
            name="username"
            type="text"
            className="login-input"
            autoComplete="username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            disabled={isSubmitting}
          />

          <label className="login-label" htmlFor="password">Password</label>
          <input
            id="password"
            name="password"
            type="password"
            className="login-input"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={isSubmitting}
          />

          <button
            type="submit"
            className="login-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Logging in...' : 'Login'}
          </button>
        </form>

        {errorMessage && <div className="login-error">{errorMessage}</div>}
      </div>
    </div>
  );
}

export default Login;